import React, { useEffect, useMemo, useState } from "react";
import ReactQuill, { Quill } from "react-quill-2";
import { register as registerCustomListItemBlot } from "./QuillExtensions/CustomListItemBlot";
import { register as registerCustomEmbedBlot } from "./QuillExtensions/CustomEmbedBlot";
import {
  OLD_EM_FONT_WHITELIST,
  OLD_REM_FONT_WHITELIST,
  BASE_PAGES_QUILL_FONT_SIZE_WHITELIST,
} from "../../../../../../components/Annotation/AnnotationTextMenu/constants";
import { useMiscUI } from "../../../../../../contexts/MiscUI/MiscUIProvider";
import { parseSemanticVersioning } from "../../../../../../utils/Versioning/versioning";
import { ObjectActionsType, useObjectsDispatch } from "../../../../../../contexts/ObjectsProvider";
const StandardFormats = [
  "background",
  "bold",
  "color",
  "font",
  "italic",
  "link",
  "size",
  "strike",
  "script",
  "underline",
  "blockquote",
  "header",
  "indent",
  "list",
  "align",
  "direction",
  "image",
  "video",
];
export const QUILL_BLOCK_CLASS_NAME = "standard-blot-block";
export const QUILL_BLOCK_TAG_NAME = "P";
export const QUILL_BLOCK_CLASS_ATTRIBUTOR_NAME = "ql-custom-size-block";
export const QUILL_INLINE_CLASS_ATTRIBUTOR_NAME = "ql-custom-size-inline";

const Parchment = Quill.import("parchment");
const Block = Quill.import("blots/block");
const TextBlot = Quill.import("blots/text");
class CustomMainBlock extends Block {}
class CustomTextBlot extends TextBlot {
  static create(value: string) {
    return document.createTextNode(value);
  }
}
CustomMainBlock.tagName = QUILL_BLOCK_TAG_NAME;
CustomMainBlock.className = QUILL_BLOCK_CLASS_NAME;

const OldCustomSizeEMStyleAttributor = new Parchment.StyleAttributor("size", "font-size", {
  //there are hard coded SCOPES within Parchment.Scope the do various different things.
  scope: Parchment.Scope.INLINE,
  whitelist: [...OLD_EM_FONT_WHITELIST, ...OLD_REM_FONT_WHITELIST],
});

const CustomSizeInlineClassAttributor = new Parchment.ClassAttributor("size", QUILL_INLINE_CLASS_ATTRIBUTOR_NAME, {
  //there are hard coded SCOPES within Parchment.Scope the do various different things.
  scope: Parchment.Scope.INLINE,
  whitelist: BASE_PAGES_QUILL_FONT_SIZE_WHITELIST,
});
const CustomSizeBlockClassAttributor = new Parchment.ClassAttributor("blockSize", QUILL_BLOCK_CLASS_ATTRIBUTOR_NAME, {
  //there are hard coded SCOPES within Parchment.Scope the do various different things.
  scope: Parchment.Scope.BLOCK,
  whitelist: BASE_PAGES_QUILL_FONT_SIZE_WHITELIST,
});
const ListItemColorStyleAttributor = new Parchment.StyleAttributor("colorListItem", "color", {
  //there are hard coded SCOPES within Parchment.Scope the do various different things.
  scope: Parchment.Scope.BLOCK,
});

function createQuillHandlers(options: any) {
  return {
    color: function color(value: string) {
      if (!this.quill) return;
      const format = this.quill.getFormat();
      if (format.list) {
        this.quill.format("colorListItem", value, "user");
      }
      this.quill.format("color", value, "user");
    },
    size: function size(value) {
      if (!this.quill) return;

      const version = parseSemanticVersioning(options.version);
      if (version) {
        const lineHeightEditVersion = version[0] >= 1 && version[1] >= 1;
        if (lineHeightEditVersion || options.isBubble) {
          this.quill.format("blockSize", value, "user");
        }
      }
      this.quill.format("size", value, "user");
    },
    clearFormat: function clearFormat() {
      if (!this.quill) return;

      const sel = this.quill.getSelection();
      this.quill.removeFormat(sel.index, sel.length, "user");
      this.quill.format("size", "normal", "user");
      this.quill.format("blockSize", "normal", "user");
    },
  } as {
    [key: string]: any;
    quill?: Quill;
  };
}

const quillKeyboardBindings = {
  custom: {
    key: "F",
    altKey: true,
    shiftKey: true,
    handler: function (range: any, context: any) {
      if (!this.quill) return;
      const len = this.quill.getLength();
      this.quill.setSelection(0, len);
      this.quill.removeFormat(0, len, "user");
      this.quill.format("size", "normal", "user");
      this.quill.format("blockSize", "normal", "user");
    },
  } as { quill?: Quill },
};

const quillHandlers = {
  //plz no delete
  // size: function size(value) {
  //     const format = this.quill.getFormat();
  //     const range = this.quill.getSelection();
  //     const blockSize = parseInt(format.blockSize, 10)
  //     const size = parseInt(format.size, 10);
  //     this.quill.format('size', value, 'user')
  //     this.quill.format('blockSize', value, 'user')
  // },
  // bold: function bold(value) {
  //     const blotList = new Set();
  //     function goThroughAllChildren(blot) {
  //         if(blot instanceof TextBlot) {
  //             return
  //          };
  //         if(blot.attributes) {
  //             blotList.add(blot);
  //         }
  //         if(blot.children) {
  //             goThroughAllChildren(blot.children.head)
  //         }
  //         if(blot.next && !(blot instanceof CustomMainBlock)) {
  //             goThroughAllChildren(blot.next)
  //         }
  //     }
  //     const {index, length} = this.quill.getSelection()
  //     const lines = this.quill.getLines(index, length)
  //     const [lineBlot] = this.quill.getLine(this.quill.getSelection().index);
  //     lines.forEach((blockBlot) => {
  //         goThroughAllChildren(blockBlot)
  //     })
  //     const attributeList = new Set();
  //     for(let blot of blotList.values()) {
  //         // if('size' in blot.attributes.attributes) {
  //         //     attributeList.add(blot.attributes.attributes.size);
  //         // }
  //         // if('size' in blot.attributes.attributes) {
  //         //     attributeList.add(blot.attributes.attributes.size);
  //         // }
  //         for(let [key, [val]] of blot.domNode.attributeStyleMap.entries()) {
  //             if(key === "font-size") {
  //                 attributeList.add(val.value.toString() + val.unit)
  //             }
  //         }
  //     }
  // }
};

const RichTextEditor = (props: any) => {
  const { toolbarId } = props;
  const objectDispatch = useObjectsDispatch();
  const fontStylingOptions = ["bold", "italic", "underline"];
  const version = parseSemanticVersioning(props.version);
  const vOneOneZero = version && version[0] >= 1 && version[1] >= 1 && version[2] >= 0;
  const fontSizeOptions = vOneOneZero
    ? [{ size: BASE_PAGES_QUILL_FONT_SIZE_WHITELIST }]
    : [{ size: OLD_REM_FONT_WHITELIST }];
  const fontColorOptions = [
    {
      color: [
        "#FFFF00",
        "#FF0000",
        "#FFC0CB",
        "#00FF00",
        "#808080",
        "#00FFFF",
        "#A8B8C8",
        "#FFBF00",
        "#FF9900",
        "#FF00FF",
        "#00B500",
        "#969DA6",
        "#00CCCC",
        "#0000FF",
        "#F2DB18",
        "#CC0000",
        "#990099",
        "#72BE44",
        "#DADBDD",
        "#0091FF",
        "#283D55",
        "#CC7722",
        "#7C0A02",
        "#5E36FF",
        "#9FFF0A",
        "#4B4F58",
        "#FFFFFF",
        "#000000",
      ],
    },
  ]; //6-15 change
  const backgroundColorOptions = [
    {
      background: [
        "#FFFF00",
        "#FF0000",
        "#FFC0CB",
        "#00FF00",
        "#808080",
        "#00FFFF",
        "#A8B8C8",
        "#FFBF00",
        "#FF9900",
        "#FF00FF",
        "#00B500",
        "#969DA6",
        "#00CCCC",
        "#0000FF",
        "#F2DB18",
        "#CC0000",
        "#990099",
        "#72BE44",
        "#DADBDD",
        "#0091FF",
        "#283D55",
        "#CC7722",
        "#7C0A02",
        "#5E36FF",
        "#9FFF0A",
        "#4B4F58",
        "#FFFFFF",
        "#000000",
        "transparent",
      ],
    },
  ]; //6-15 change
  const alignOptions = [{ align: "" }, { align: "center" }, { align: "right" }];
  const listOptions = [{ list: "bullet" }, { list: "ordered" }, "clearFormat"];
  const [theme, setTheme] = useState("snow");
  const [{ quillRefreshToggle }] = useMiscUI();
  const bubbleToolbar = [
    fontSizeOptions,
    fontStylingOptions,
    fontColorOptions,
    backgroundColorOptions,
    alignOptions,
    listOptions,
  ];

  // const [isLoading, setIsLoading] = useState(true);

  if (props.attached) {
    if (theme === "bubble" && props.attached === "true") {
      setTheme("snow");
    }
  }

  useEffect(() => {
    if (vOneOneZero) {
      Quill.register("formats/size", CustomSizeInlineClassAttributor);
      Quill.register("formats/blockSize", CustomSizeBlockClassAttributor);
      //below if we want bubble theme to be on each click
      // Quill.register('themes/bubble', ExtendBubbleTheme);
    } else {
      Quill.register("formats/size", OldCustomSizeEMStyleAttributor);
    }
    Quill.register("formats/color", ListItemColorStyleAttributor);
    Quill.register("blots/block", CustomMainBlock);
    Quill.register("blots/text", CustomTextBlot);
    // Register custom Blots
    registerCustomListItemBlot(Quill);
    registerCustomEmbedBlot(Quill);
  }, [quillRefreshToggle]);
  const isBubble = props.theme === "bubble";
  const quillModules = useMemo(() => {
    return {
      toolbar: {
        container: isBubble ? bubbleToolbar : toolbarId,
        handlers: createQuillHandlers(
          {
            version: props?.version,
            isBubble,
          } /**options */,
        ),
      },
      clipboard: {
        matchers: [
          [
            Node.TEXT_NODE,
            (node, delta) => {
              if (node.data.match(/[^\n\S]|\t/)) {
                const Delta = Quill.import("delta");
                return new Delta().insert(node.data);
              }
              return delta;
            },
          ],
        ],
      },
      keyboard: {
        bindings: quillKeyboardBindings,
      },
    };
  }, [props?.version]);
  // useEffect(() => {
  //   if (!isLoading) {
  //     if (props.quillRef && props.quillRef?.current) {
  //       props.quillRef.current.editor.root.innerHTML = props.displayText;
  //       // props.quillRef.current.focus();
  //     }
  //   }
  // }, [isLoading]);

  useEffect(() => {
    if (props.quillRef && props.quillRef?.current) {
      // props.quillRef.current.regenerate();
      // const clipboard = props.quillRef.current.getEditor().clipboard;
      // const stuff = clipboard.convert({ html: props.displayText });
      // props.quillRef.current.getEditor().setContents([]);
      // props.quillRef.current.getEditor().setContents(stuff);
    }
  }, [quillRefreshToggle]);
  const assignRawInput = (newText: any, delta: any, source: any, editor: any) => {
    if (source === "user") {
      let formattedString = newText;
      if (props?.version === undefined) {
        formattedString = formatUnicodeWithinString(newText);
      }

      objectDispatch({
        type: ObjectActionsType.UPDATE_TEXT_BOX,
        payload: {
          objectId: props.objectId,
          html: formattedString,
        },
      });
    }
  };

  function handleChangeSelection(range: any, source: any, editor: any) {
    if (props.handleSelectionChange) {
      props.handleSelectionChange(range, source, editor);
    }
  }
  const formatUnicodeWithinString = (inputString: any) => {
    //recursivly iterates through a string and converts unicode characters
    if (
      inputString.length > 0 &&
      inputString.includes("\\u") &&
      inputString.replace(/<[^>]*>?/gm, "").length - 5 > inputString.replace(/<[^>]*>?/gm, "").indexOf("\\u")
    ) {
      //unicode detected
      const testvar = inputString.slice(inputString.indexOf("\\u"), inputString.indexOf("\\u") + 6); //slices out the unicode then formats it now it needs to be put back into the string at position
      const formattedCode = String.fromCodePoint(Number.parseInt(testvar.slice(2, 6), 16));
      const convertedInput = [
        inputString.slice(0, inputString.indexOf("\\u")),
        formattedCode,
        inputString.slice(inputString.indexOf("\\u") + 6, inputString.length),
      ].join("");
      if (convertedInput.includes("\\u")) {
        //if there are still unformatted codes the function executes again
        return formatUnicodeWithinString(convertedInput);
      } else {
        return convertedInput;
      }
    } else {
      return inputString;
    }
  };

  return (
    <ReactQuill
      id="editor"
      style={{
        pointerEvents: "all",
      }}
      ref={props.quillRef}
      theme={props.theme}
      value={props.displayText}
      placeholder="Enter Text Here"
      className="max-height-one-hundred-percent heighBox"
      onChange={assignRawInput}
      formats={StandardFormats}
      preserveWhitespace={true}
      modules={quillModules}
      onChangeSelection={handleChangeSelection}
      readOnly={props?.readOnly ?? false}
      onFocus={() => {
        props.onFocus?.();
      }}
    />
  );
};

export default RichTextEditor;
